import { Divider, Flex, Steps, Tooltip, Typography } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Accordion from 'components/Accordion';
import CardComponent from 'components/Card';
import CustomFileList from 'components/CustomFileList';
import DrawerComponent from 'components/Drawer';
import TaskInfo from 'components/TaskInfo';
import { StatusTag } from '../table/StatusTag';

import {
  shipmentStatusMap,
  statusMap,
} from 'screens/deliveries/package-tracker/helper';
import useSalesOrderDetailQuery from 'screens/deliveries/package-tracker/hooks/useSalesOrderDetailQuery';
import { renderDate, renderInfoValue } from 'utils/helpers';

import type { PackageDTO } from 'screens/deliveries/package-tracker/types';

interface PackageDetailsDrawerProps {
  pkg: PackageDTO;
  isOpen: boolean;
  onClose: () => void;
}

const { Title, Text } = Typography;

const PackageDetailsDrawer = ({
  pkg,
  isOpen,
  onClose,
}: PackageDetailsDrawerProps) => {
  const { t } = useTranslation();

  const { data: order, isLoading } = useSalesOrderDetailQuery(
    pkg?.salesOrder?.orderId as string,
  );

  const relevantPackageFromSalesOrder = useMemo(
    () => order?.packageOrders?.find((p) => p.packageId === pkg.id),
    [order, pkg],
  );

  const getCurrentStep = () => {
    const indexMap = {
      shipped: 1,
      delivered: 2,
    };

    if (pkg?.invoicePrimaryId) return 3;

    return indexMap[pkg?.status?.toLowerCase() as keyof typeof indexMap] || 0;
  };

  const packageInvoiceNumbers = useMemo(() => {
    return relevantPackageFromSalesOrder?.invoices?.map(
      (invoice) => invoice?.invoiceNumber,
    );
  }, [relevantPackageFromSalesOrder]);

  const unshippedPackageItems = useMemo(() => {
    if (!order?.orderItems) return [];

    const packageItemsMap = new Map(
      relevantPackageFromSalesOrder?.packageItems?.map((item) => [
        item?.itemId,
        item?.quantity || 0,
      ]) || [],
    );

    return order?.orderItems?.reduce(
      (result, item) => {
        const shippedQty = packageItemsMap.get(item?.itemId) || 0;
        if (shippedQty < item?.quantity) {
          result.push({
            ...item,
            quantity: item?.quantity - shippedQty,
          });
        }
        return result;
      },
      [] as typeof order.orderItems,
    );
  }, [order, relevantPackageFromSalesOrder]);

  return (
    <DrawerComponent
      loading={isLoading}
      title={t('packageTrackerDashboard.drawer.packageDetails')}
      open={isOpen}
      onClose={onClose}
      content={
        <Flex vertical gap={8}>
          <CardComponent
            className={'package-details-drawer-card'}
            cardContent={
              /* Package Details */
              <Flex vertical gap={8}>
                <Flex align="center" wrap="wrap" gap={8}>
                  <TaskInfo
                    renderHeader={() => (
                      <Title level={4} className="m-0" style={{ fontSize: 18 }}>
                        {renderInfoValue(pkg?.packageNumber, 'N/A')}
                      </Title>
                    )}
                    renderValue={() => (
                      <Text type="secondary">
                        {t('packageTrackerDashboard.drawer.salesOrder')}:{' '}
                        {renderInfoValue(pkg?.salesOrder?.orderNumber, 'N/A')}
                      </Text>
                    )}
                  />
                  <StatusTag
                    status={pkg?.invoicePrimaryId ? 'fulfilled' : pkg?.status}
                    statusConfigMap={statusMap}
                  />
                </Flex>
                <Divider />

                <Flex wrap="wrap" style={{ rowGap: 20 }}>
                  <TaskInfo
                    header={t('packageTrackerDashboard.table.agronomist')}
                    value={pkg?.agronomist?.name}
                  />
                  <TaskInfo
                    header={t('packageTrackerDashboard.drawer.farmerGroup')}
                    value={order?.cubeName}
                  />
                  <TaskInfo
                    header={t('packageTrackerDashboard.drawer.createdDate')}
                    value={renderDate(pkg?.packageDate, 'N/A')}
                  />

                  {/* commented out as the value is always null in api, will be added later if needed */}
                  {/* <TaskInfo
                    header={t('packageTrackerDashboard.drawer.estDeliveryDate')}
                    value={pkg?.expectedDeliveryDate}
                  /> 
                  */}

                  <TaskInfo
                    header={t('packageTrackerDashboard.table.shipmentStatus')}
                    renderValue={() => {
                      const config =
                        shipmentStatusMap?.[
                          order?.status?.toLowerCase() as keyof typeof shipmentStatusMap
                        ];
                      return (
                        <Flex gap={6} align="center">
                          <span>{config?.label || 'N/A'}</span>
                          <Tooltip title={config?.tooltip || 'N/A'}>
                            {config?.icon}
                          </Tooltip>
                        </Flex>
                      );
                    }}
                  />

                  <TaskInfo
                    header={t('packageTrackerDashboard.drawer.invoice')}
                    value={
                      <Flex vertical>
                        {packageInvoiceNumbers?.length
                          ? packageInvoiceNumbers?.map((invoice) => (
                              <Text key={invoice}>{invoice}</Text>
                            ))
                          : 'N/A'}
                      </Flex>
                    }
                  />
                </Flex>

                {/* Shipped Items */}

                {!!relevantPackageFromSalesOrder?.packageItems?.length && (
                  <Accordion
                    title={t('packageTrackerDashboard.drawer.shippedItems')}
                    className="w-100 mt-4"
                  >
                    <Flex vertical gap={8}>
                      {relevantPackageFromSalesOrder?.packageItems?.map(
                        (item) => (
                          <Flex key={item?.itemId} justify="space-between">
                            <Text style={{ color: '#545454' }}>
                              {item?.name}
                            </Text>
                            <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                              {item?.quantity ? 'x' : ''}
                              {Math.round(item?.quantity)}
                            </Text>
                          </Flex>
                        ),
                      )}
                    </Flex>

                    {relevantPackageFromSalesOrder?.packageItems?.length > 0 &&
                      unshippedPackageItems?.length > 0 && <Divider />}

                    {/* Unshipped Items */}

                    {unshippedPackageItems?.length > 0 && (
                      <Flex vertical gap={8} className="mt-4">
                        <Text style={{ fontSize: 14 }} className="mb-2">
                          {t('packageTrackerDashboard.drawer.unshippedItems')}
                        </Text>
                        {unshippedPackageItems?.map((item) => (
                          <Flex key={item?.itemId} justify="space-between">
                            <Text style={{ color: '#545454' }}>
                              {item?.name}
                            </Text>
                            <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                              {item?.quantity ? 'x' : ''}
                              {Math.round(item?.quantity)}
                            </Text>
                          </Flex>
                        ))}
                      </Flex>
                    )}
                  </Accordion>
                )}

                <Divider />

                {/* Package Progress */}

                <Flex vertical gap={10}>
                  <Title level={5} style={{ fontSize: 14 }}>
                    {t('packageTrackerDashboard.drawer.packageProgress')}
                  </Title>

                  <Steps
                    direction="vertical"
                    size="default"
                    style={{ height: 250 }}
                    current={getCurrentStep()}
                  >
                    <Steps.Step
                      title={
                        <Text style={{ fontSize: '16px' }}>
                          {t('packageTrackerDashboard.packageStatus.shipping')}
                        </Text>
                      }
                      description={renderDate(pkg?.packageDate, 'N/A')}
                    />
                    <Steps.Step
                      title={
                        <Text style={{ fontSize: '16px' }}>
                          {t('packageTrackerDashboard.packageStatus.delivered')}
                        </Text>
                      }
                      description={renderDate(
                        pkg?.shipmentDeliveredDate,
                        'N/A',
                      )}
                    />

                    <Steps.Step
                      title={
                        <Text style={{ fontSize: '16px' }}>
                          {t(
                            'packageTrackerDashboard.packageStatus.invoiceUploaded',
                          )}
                        </Text>
                      }
                      description={renderDate(
                        relevantPackageFromSalesOrder?.invoices?.[0]
                          ?.signedInvoiceImages?.[0]?.lastModifiedDate,
                        'N/A',
                      )}
                    />
                  </Steps>
                </Flex>

                {/* Signed Invoices */}
                <Flex vertical gap={8}>
                  <Title level={5} style={{ fontSize: 14 }}>
                    {t('packageTrackerDashboard.drawer.signedInvoices')}
                  </Title>

                  {relevantPackageFromSalesOrder?.invoices?.length
                    ? relevantPackageFromSalesOrder?.invoices?.map(
                        (invoice) => (
                          <Accordion
                            title={invoice?.invoiceNumber}
                            key={invoice?.id}
                          >
                            <Flex vertical gap={14}>
                              <TaskInfo
                                header={t(
                                  'packageTrackerDashboard.drawer.photos',
                                )}
                                labelClass="mb-2"
                                renderValue={() => (
                                  <Flex
                                    className="max-w-100 overflow-auto"
                                    gap={10}
                                  >
                                    <CustomFileList
                                      images={invoice?.signedInvoiceImages?.map(
                                        (image) => image?.url,
                                      )}
                                    />
                                    {!invoice?.signedInvoiceImages?.length
                                      ? 'N/A'
                                      : ''}
                                  </Flex>
                                )}
                              />

                              <TaskInfo
                                labelClass="mb-1"
                                header={t(
                                  'packageTrackerDashboard.drawer.note',
                                )}
                                renderValue={() => (
                                  <Text
                                    style={{
                                      color: '#545454',
                                      whiteSpace: 'break-spaces',
                                    }}
                                  >
                                    {invoice?.signedInvoiceNotes || 'N/A'}
                                  </Text>
                                )}
                              />
                            </Flex>
                          </Accordion>
                        ),
                      )
                    : 'N/A'}
                </Flex>
              </Flex>
            }
            loading={isLoading}
          />

          {isLoading ? (
            <CardComponent
              className={'package-details-drawer-card mt-4'}
              loading={true}
              cardContent={<></>}
            />
          ) : (
            <>
              {/* Sales order items Items */}
              {!!order?.orderItems?.length && (
                <Accordion
                  title={t('packageTrackerDashboard.drawer.salesOrderItems')}
                  className="w-100 mt-4 package-details-drawer-card px-2"
                >
                  <Flex vertical gap={8}>
                    {order?.orderItems?.map((item) => (
                      <Flex key={item?.itemId} justify="space-between">
                        <Text style={{ color: '#545454' }}>{item?.name}</Text>
                        <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                          {item?.quantity ? 'x' : ''}
                          {Math.round(item?.quantity)}
                        </Text>
                      </Flex>
                    ))}
                  </Flex>
                </Accordion>
              )}
            </>
          )}
        </Flex>
      }
    />
  );
};

export default PackageDetailsDrawer;
